import { useEffect, useState } from 'react';
import { Table, Select, Modal, Form, Upload, Input, Button, Row, DatePicker, Tabs } from 'antd';
import AssignmentService from '../services/AssignmentService';
import { formatDate, openNotification } from '../utils';
import StudentService from '../services/StudentService';
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  UploadOutlined
} from '@ant-design/icons';
import AssignmentDetail from '../components/Assignments/AssignmentDetail';
import LessonService from '../services/LessonService';
import type { UploadFile, UploadProps } from 'antd';
import dayjs from 'dayjs';
import Filter, { type AssignmentFilterValues } from '../components/Assignments/Filter';

const { Option } = Select;

const Assignments = () => {
  const [loading, setLoading] = useState(false);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [lessons, setLessons] = useState<any>([]);
  const [referenceDataLoading, setReferenceDataLoading] = useState(false);
  const [referenceDataLoaded, setReferenceDataLoaded] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  let authId = localStorage.getItem('id');
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [activeTab, setActiveTab] = useState<'active' | 'expired'>('active');
  const [filter, setFilter] = useState<AssignmentFilterValues>({});

  const getAssignments = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.set('isExpired', String(activeTab === 'expired'));
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.set(key, String(value));
        }
      });
      const queryString = queryParams.toString();
      const response = await AssignmentService.getAll(queryString ? `?${queryString}` : '');
      setAssignments(response?.data ?? []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadReferenceData = async () => {
    if (referenceDataLoaded || referenceDataLoading) {
      return;
    }

    setReferenceDataLoading(true);
    try {
      const [lessonResponse, studentResponse] = await Promise.all([
        LessonService.getLiteList(),
        StudentService.getAll(),
      ]);
      setLessons(lessonResponse?.data ?? []);
      setStudents(studentResponse?.data ?? []);
      setReferenceDataLoaded(true);
    } catch (error) {
      console.error('Ödev oluşturma seçenekleri alınamadı:', error);
    } finally {
      setReferenceDataLoading(false);
    }
  };

  useEffect(() => {
    getAssignments();
  }, [activeTab, filter]);

  const columns = [
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (_: any, record: any) => (
        <>
          {record.isDone ?
            <Row align={'middle'}>
              <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: 24, marginRight: 5 }} />Tamamlandı
            </Row>
            : record.isExpired ?
              <Row align={'middle'}>
                <CloseCircleTwoTone twoToneColor="#eb2f96" style={{ fontSize: 24, marginRight: 5 }} />Süresi Doldu
              </Row>
              : record?.responseContent || record?.responseImage ?
                <Row align={'middle'}>
                  <CloseCircleTwoTone twoToneColor="#eb2f96" style={{ fontSize: 24, marginRight: 5 }} />Eksik veya Hatalı
                </Row>
                :
            <Row align={'middle'}>
              <CloseCircleTwoTone twoToneColor="#eb2f96" style={{ fontSize: 24, marginRight: 5 }} />Cevaplanmadı
            </Row>
          }
        </>
      )
    },
    {
      title: 'Ders Adı',
      dataIndex: 'lessonName',
      key: 'lessonName',
    },
    {
      title: 'Öğretmen Adı',
      dataIndex: 'teacherName',
      key: 'teacherName',
    },
    {
      title: 'Atanan Öğrenci',
      dataIndex: 'studentName',
      key: 'studentName'
    },
    {
      title: 'Ödev Verilme Tarihi',
      dataIndex: 'createdAt',
      render: (text: any) => (
        <>
          {formatDate(text)}
        </>
      )
    },
    {
      title: 'Ödev Tarihi',
      dataIndex: 'dueDate',
      render: (text: any) => (
        <>
          {formatDate(text)}
        </>
      )
    },
    {
      title: 'Detay',
      key: 'action',
      render: (_: any, record: any) => (
        <AssignmentDetail item={record} getLessonsAndAssignments={getAssignments} />
      )
    },

  ];

  const showModal = () => {
    setIsModalVisible(true);
    void loadReferenceData();
  };

  const handleCreate = async () => {
    let values: any;

    try {
      values = await form.validateFields();
    } catch (error) {
      console.log('Validate Failed:', error);
      return;
    }

    const postObj = {
      userId: parseInt(authId || '0', 10),
      studentId: parseInt(values.studentId || '0', 10),
      lessonId: parseInt(values.lessonId || '0', 10),
      dueDate: values.dueDate.format('YYYY-MM-DDTHH:mm'),
      content: values.content,
    };

    try {
      await AssignmentService.create(postObj, fileList[0]);
      form.resetFields();
      setFileList([]);
      openNotification('success', 'Başarılı', 'Ödev Oluşturuldu.');
      void getAssignments();
      setIsModalVisible(false);
    } catch (error) {
      console.error(error);
      openNotification('error', 'Başarısız', 'Ödev oluşturulamadı. Lütfen bilgileri kontrol edip tekrar deneyin.');
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setIsModalVisible(false);
    setFileList([]);
  };

  const props: UploadProps = {
    onRemove: () => {
      setFileList([]);
    },
    beforeUpload: (file) => {
      if (fileList.length >= 1) {
        openNotification('warning', 'Uyarı', 'Sadece bir adet dosya yükleyebilirsiniz')
        return Upload.LIST_IGNORE;
      }
      setFileList([file]);
      return false;
    },
    fileList,
    multiple: false,
    maxCount: 1,
  };


  const handleOnChangeFilter = (values: AssignmentFilterValues) => setFilter(values);

  return (
    <div>
      <Row justify={'space-between'} style={{ marginBottom: 16 }}>
        <Button type="default" className='bg-saha-purple  font-color-white' onClick={showModal} style={{ marginBottom: 16 }}>
          Ödev Oluştur
        </Button>
        <Filter
          filter={filter}
          lessons={lessons}
          loading={referenceDataLoading}
          onChangeFilter={handleOnChangeFilter}
          onOpen={loadReferenceData}
          students={students}
        />
      </Row>
      <Tabs
        activeKey={activeTab}
        items={[
          { key: 'active', label: 'Aktif Ödevler' },
          { key: 'expired', label: 'Süresi Geçenler' },
        ]}
        onChange={(key) => setActiveTab(key as 'active' | 'expired')}
      />
      <Table
        loading={loading}
        dataSource={assignments}
        columns={columns}
        rowKey="id"
        scroll={{ x: 'max-content' }}
      />
      <Modal title="Yeni Ödev Oluştur" open={isModalVisible} onOk={handleCreate} onCancel={handleCancel} okText="Oluştur" cancelText="İptal"
        okButtonProps={{ className: 'bg-saha-purple  font-color-white', disabled: referenceDataLoading, type: 'default' }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="lessonId" label="Ders" rules={[{ required: true, message: 'Ders seçiniz' }]}>
            <Select loading={referenceDataLoading} style={{ marginBottom: 16 }} placeholder="Seçiniz" allowClear onClear={() => (undefined)}>
              {lessons.map((lesson: any) => (
                <Option key={lesson.id} value={lesson.id}>{lesson.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="studentId" label="Öğrenci" rules={[{ required: true, message: 'Öğrenci seçiniz' }]}>
            <Select loading={referenceDataLoading} style={{ marginBottom: 16 }} placeholder="Seçiniz" allowClear onClear={() => (undefined)}>
              {students.map((student: any) => (
                <Option key={student.id} value={student.id}>{student.firstName} {student.lastName}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="dueDate"
            label="Ödev Tarihi"
            rules={[
              {
                required: true,
                message: 'Ödev Tarihi zorunlu',
              }]}
          >
            <DatePicker
              className='w-full'
              format="DD/MM/YYYY HH:mm"
              showTime={{ defaultValue: dayjs('00:00', 'HH:mm'), minuteStep: 5 }}
            />
            {/* {formatDate(exam.examDate)} */}
          </Form.Item>
          <Form.Item name="content" label="Ödev İçerik" rules={[{ required: true, message: 'İçerik giriniz' }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Ödev Dosyası" extra="Tek bir dosya ekleyebilirsiniz.">
            <Upload {...props} listType="text">
              <Button icon={<UploadOutlined />}>Dosya Seç</Button>
            </Upload>
          </Form.Item>

        </Form>
      </Modal>
    </div>
  );
};

export default Assignments;
